import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { validateResponse } from "@/api/validateResponse";
import {
  ProgramListResponseSchema,
  PublicClassSectionListResponseSchema,
  PublicRegistrationResponseSchema,
  TermListResponseSchema,
  type AgeBand,
  type Program,
  type PublicClassSection,
  type PublicRegistrationInput,
  type PublicRegistrationResponse,
  type Term,
} from "@/api/types/classRegistration";

/**
 * Dedicated axios instance for the PUBLIC class-registration surface —
 * same rationale as usePortal.ts's `portalClient` / useMembershipPortal.ts's
 * `membershipPortalClient`: this is a completely separate, unauthenticated
 * backend router (app/api/public_class_registration.py, mounted at
 * /api/public/class-registration, OUTSIDE /api/v2 entirely — no bearer
 * token, no cookie, no /login redirect on a 4xx). Never the staff
 * `apiClient`.
 */
const classRegistrationPublicClient = axios.create({
  baseURL: "/api/public/class-registration",
  headers: { "Content-Type": "application/json" },
});

export const classRegistrationPublicKeys = {
  programs: ["public-class-registration", "programs"] as const,
  terms: ["public-class-registration", "terms"] as const,
  sections: (params: Record<string, unknown>) => ["public-class-registration", "sections", params] as const,
};

export function usePublicPrograms() {
  return useQuery<Program[]>({
    queryKey: classRegistrationPublicKeys.programs,
    queryFn: async () => {
      const { data } = await classRegistrationPublicClient.get("/programs");
      return validateResponse(ProgramListResponseSchema, data, "GET /public/class-registration/programs").items;
    },
    staleTime: 60 * 1000,
  });
}

export function usePublicTerms() {
  return useQuery<Term[]>({
    queryKey: classRegistrationPublicKeys.terms,
    queryFn: async () => {
      const { data } = await classRegistrationPublicClient.get("/terms");
      return validateResponse(TermListResponseSchema, data, "GET /public/class-registration/terms").items;
    },
    staleTime: 60 * 1000,
  });
}

interface UsePublicClassSectionsParams {
  term_id?: string;
  program_id?: string;
  age_band?: AgeBand;
}

export function usePublicClassSections(params: UsePublicClassSectionsParams) {
  const { term_id, program_id, age_band } = params;
  return useQuery<PublicClassSection[]>({
    queryKey: classRegistrationPublicKeys.sections({ term_id, program_id, age_band }),
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (term_id) searchParams.set("term_id", term_id);
      if (program_id) searchParams.set("program_id", program_id);
      if (age_band) searchParams.set("age_band", age_band);
      const { data } = await classRegistrationPublicClient.get(`/sections?${searchParams.toString()}`);
      return validateResponse(
        PublicClassSectionListResponseSchema,
        data,
        "GET /public/class-registration/sections",
      ).items;
    },
    enabled: !!term_id,
    staleTime: 10 * 1000,
  });
}

export function useRegisterForClass() {
  const queryClient = useQueryClient();
  return useMutation<PublicRegistrationResponse, unknown, PublicRegistrationInput>({
    mutationFn: async (input) => {
      const { data } = await classRegistrationPublicClient.post("/register", input);
      return validateResponse(
        PublicRegistrationResponseSchema,
        data,
        "POST /public/class-registration/register",
      );
    },
    onSuccess: () => {
      // A successful registration changes seats_remaining/waitlist_open
      // for the section just filled — without this, a guardian
      // registering a second child back-to-back (e.g. siblings) would see
      // a stale seat count until the 10s staleTime lapses on its own.
      void queryClient.invalidateQueries({ queryKey: ["public-class-registration", "sections"] });
    },
  });
}
